import { combineReducers } from 'redux';

import { emptyArray, emptyObject } from 'Lib/constants/placeholder';
import { createKeyInTransform } from 'Lib/persist/transforms';
import type { Reducer } from 'Lib/reduxType';

import {
  FETCH_CONSENTS_SUCCESS,
  PRIVACY_SETTINGS_PROMPT,
  UPDATE_CONSENTS_ERROR,
  UPDATE_CONSENTS_SUCCESS,
} from './constants';
import initialState from './initialState';
import type { PrivacyState } from './types';

// @TODO: Remove when backend no longer sends empty 200s
function hasConsentData(payload) {
  return payload?.result?.categoryData && payload?.result?.consentData;
}

const categoryDataReducer: Reducer<PrivacyState['categoryData']> = (
  state = initialState.categoryData,
  action
) => {
  switch (action.type) {
    case FETCH_CONSENTS_SUCCESS:
      return hasConsentData(action.payload)
        ? action.payload?.result?.categoryData ?? emptyObject
        : state;
    default:
      return state;
  }
};

const categoryOrderReducer: Reducer<PrivacyState['categoryOrder']> = (
  state = initialState.categoryOrder,
  action
) => {
  switch (action.type) {
    case FETCH_CONSENTS_SUCCESS:
      return hasConsentData(action.payload)
        ? action.payload?.result?.categoryOrder ?? emptyArray
        : state;
    default:
      return state;
  }
};

const consentDataReducer: Reducer<PrivacyState['consentData']> = (
  state = initialState.consentData,
  action
) => {
  switch (action.type) {
    case FETCH_CONSENTS_SUCCESS:
      return hasConsentData(action.payload)
        ? action.payload?.result?.consentData ?? emptyObject
        : state;
    default:
      return state;
  }
};

const consentsReducer: Reducer<PrivacyState['consents']> = (
  state = initialState.consents,
  action
) => {
  switch (action.type) {
    case FETCH_CONSENTS_SUCCESS:
    case UPDATE_CONSENTS_SUCCESS:
      return hasConsentData(action.payload) ||
        Object.keys(action.payload?.result?.consents ?? emptyObject).length
        ? action.payload?.result?.consents ?? emptyObject
        : state;
    default:
      return state;
  }
};

const hasDeferredUpdateReducer: Reducer<PrivacyState['hasDeferredUpdate']> = (
  state = initialState.hasDeferredUpdate,
  action
): boolean => {
  switch (action.type) {
    case UPDATE_CONSENTS_SUCCESS:
      return action.payload?.deferredUpdate ?? state;
    default:
      return state;
  }
};

const showPromptReducer: Reducer<PrivacyState['showPrompt']> = (
  state = initialState.showPrompt,
  action
) => {
  switch (action.type) {
    case PRIVACY_SETTINGS_PROMPT:
      return true;
    case UPDATE_CONSENTS_SUCCESS:
    case UPDATE_CONSENTS_ERROR:
      return false;
    default:
      return state;
  }
};

export const reducer = combineReducers({
  categoryData: categoryDataReducer,
  categoryOrder: categoryOrderReducer,
  consentData: consentDataReducer,
  consents: consentsReducer,
  hasDeferredUpdate: hasDeferredUpdateReducer,
  showPrompt: showPromptReducer,
});

export const persistConfig = {
  transforms: [
    createKeyInTransform([
      'categoryData',
      'categoryOrder',
      'consentData',
      'consents',
      'hasDeferredUpdate',
      'showPrompt',
    ]),
  ],
  version: 1,
};
